import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Sliders, GitMerge, ClipboardList, Settings, Zap, LogOut, Image, Bell, LineChart as LineChartIcon, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/recommendations", label: "Recommendations", icon: Sparkles, testid: "nav-recommendations" },
  { to: "/meta-ads", label: "Meta Ads", icon: Image, testid: "nav-meta-ads" },
  { to: "/trends", label: "Trends", icon: LineChartIcon, testid: "nav-trends" },
  { to: "/rules", label: "Rules", icon: Sliders, testid: "nav-rules" },
  { to: "/approvals", label: "Approvals", icon: GitMerge, testid: "nav-approvals" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications", showBadge: true },
  { to: "/activity", label: "Activity", icon: ClipboardList, testid: "nav-activity" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const r = await api.get("/notifications");
        if (mounted) setUnread(r.data?.unread || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 60_000); // poll every 60s
    return () => { mounted = false; clearInterval(id); };
  }, []);
  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 flex">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className="w-60 shrink-0 bg-white border-r border-zinc-200 flex flex-col sticky top-0 h-screen"
      >
        <div className="px-6 py-6 border-b border-zinc-200">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-zinc-900 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-base tracking-tight uppercase">MetricFlow</span>
          </Link>
          <div className="data-label mt-2">CPA Budget Automation</div>
        </div>
        <nav className="flex-1 py-4">
          {nav.map(({ to, label, icon: Icon, end, testid, showBadge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-zinc-900 bg-zinc-100 font-medium text-zinc-900"
                    : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              <span className="flex-1">{label}</span>
              {showBadge && unread > 0 && (
                <span
                  data-testid="nav-notifications-badge"
                  className="min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-600 text-white text-[10px] font-mono font-semibold rounded-none"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-6 border-t border-zinc-200 space-y-3">
          {user && (
            <div className="flex items-center gap-2" data-testid="user-info">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 border border-zinc-200" />
              ) : (
                <div className="w-7 h-7 bg-zinc-900 text-white flex items-center justify-center text-xs font-mono uppercase">
                  {user.email?.[0] || "?"}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">{user.name || user.email}</div>
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
                >
                  <LogOut className="w-3 h-3" /> Sign out
                </button>
              </div>
            </div>
          )}
          <div className="data-label">v1.2 · Live writes</div>
          <div className="text-[10px] text-zinc-500 font-mono">TM · Meta · Google · Slack</div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
